// src/pages/admin/UsersPage.js
import React, { useState, useEffect, useCallback } from "react";
import authService from "../../services/authService";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../components/common/Toast";
import { IconUsers, IconPlus, IconX, IconShield, IconLayers, IconTrash, IconCalendar } from "../../components/common/Icons";

const LEVEL_NAMES = { 1: "Superuser", 2: "Director", 3: "Co-Director", 4: "Coach", 5: "Asst Coach", 6: "Manager", 7: "Scorekeeper", 8: "Viewer" };

function Modal({ open, onClose, title, children, wide }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div className={`bg-onyx-900 border border-onyx-700/50 rounded-2xl shadow-2xl mx-4 max-h-[90vh] overflow-y-auto animate-fade-in-up ${wide ? "w-full max-w-2xl" : "w-full max-w-lg"}`}
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-onyx-800/50 sticky top-0 bg-onyx-900 z-10">
          <h2 className="font-display font-semibold text-onyx-100 tracking-wide">{title}</h2>
          <button onClick={onClose} className="p-1 text-onyx-500 hover:text-onyx-200"><IconX size={20} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
}

export default function UsersPage() {
  const { user: me } = useAuth();
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [allPerms, setAllPerms] = useState([]);
  const [allGroups, setAllGroups] = useState([]);
  const [allTeams, setAllTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showCreate, setShowCreate] = useState(false);
  const [newUser, setNewUser] = useState({ username: "", password: "", email: "", level: 4 });

  // Grant modal
  const [grantTarget, setGrantTarget] = useState(null);
  const [grantData, setGrantData] = useState({ permission_id: "", team_id: "", expires_at: "", note: "" });
  const [targetGrants, setTargetGrants] = useState([]);
  const [targetGroups, setTargetGroups] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    const [u, p, g, t] = await Promise.all([
      authService.getUsers(), authService.getPermissions(),
      authService.getGroups(), authService.getTeams(),
    ]);
    if (u.success) setUsers(u.data);
    if (p.success) setAllPerms(p.data);
    if (g.success) setAllGroups(g.data);
    if (t.success) setAllTeams(t.data);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    const res = await authService.createUser(newUser);
    if (res.success) {
      toast("User created", "success");
      setShowCreate(false);
      setNewUser({ username: "", password: "", email: "", level: 4 });
      load();
    } else {
      const msg = typeof res.error === "object" ? Object.values(res.error).flat().join(", ") : String(res.error);
      toast(msg, "error");
    }
  };

  const openGrants = async (target) => {
    const [gr, gp] = await Promise.all([
      authService.getUserGrants(target.id),
      authService.getUserGroups(target.id),
    ]);
    if (gr.success) setTargetGrants(gr.data.grants || []);
    if (gp.success) setSelectedGroupIds(gp.data.groups?.map((g) => g.id) || []);
    setGrantTarget(target);
  };

  const handleGrant = async () => {
    if (!grantData.permission_id) return toast("Select a permission", "error");
    const grant = {
      permission_id: parseInt(grantData.permission_id),
      team_id: grantData.team_id ? parseInt(grantData.team_id) : null,
      expires_at: grantData.expires_at || null,
      note: grantData.note,
    };
    const res = await authService.grantPermissions(grantTarget.id, [grant]);
    if (res.success) {
      toast("Permission granted", "success");
      setGrantData({ permission_id: "", team_id: "", expires_at: "", note: "" });
      const gr = await authService.getUserGrants(grantTarget.id);
      if (gr.success) setTargetGrants(gr.data.grants || []);
      load();
    } else {
      const msg = typeof res.error === "object" ? JSON.stringify(res.error) : String(res.error);
      toast(msg, "error");
    }
  };

  const handleRevoke = async (grantId) => {
    const res = await authService.revokeGrants(grantTarget.id, [grantId]);
    if (res.success) {
      toast("Grant revoked", "success");
      setTargetGrants((prev) => prev.filter((g) => g.id !== grantId));
      load();
    } else {
      toast("Failed to revoke", "error");
    }
  };

  const handleSaveGroups = async () => {
    const res = await authService.setUserGroups(grantTarget.id, selectedGroupIds);
    if (res.success) {
      toast("Groups updated", "success");
      load();
    } else {
      const msg = typeof res.error === "object" ? JSON.stringify(res.error) : String(res.error);
      toast(msg, "error");
    }
  };

  const toggleGroup = (id) => setSelectedGroupIds((g) => g.includes(id) ? g.filter((x) => x !== id) : [...g, id]);

  const inputCls = "w-full px-3 py-2.5 bg-onyx-950 border border-onyx-700/50 rounded-lg text-onyx-100 font-body text-sm placeholder-onyx-600 focus:outline-none focus:border-tiger-500/50 transition-all";
  const labelCls = "block text-xs font-display text-onyx-500 tracking-wider uppercase mb-1.5";
  const btnPrimary = "px-4 py-2.5 rounded-lg font-display font-semibold text-sm tracking-wider bg-gradient-to-r from-tiger-600 to-tiger-500 text-white hover:from-tiger-500 hover:to-tiger-400 transition-all";

  if (loading) {
    return <div className="max-w-7xl mx-auto px-4 py-20 text-center"><div className="inline-block w-8 h-8 border-2 border-tiger-500/30 border-t-tiger-500 rounded-full animate-spin" /></div>;
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-10">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <IconUsers size={22} className="text-tiger-500" />
          <h1 className="font-display text-2xl font-bold text-onyx-100 tracking-wider">MANAGE USERS</h1>
        </div>
        <button onClick={() => setShowCreate(true)} className={`flex items-center gap-2 ${btnPrimary}`}>
          <IconPlus size={16} /> ADD USER
        </button>
      </div>

      {/* Users table */}
      <div className="rounded-xl border border-onyx-800/40 bg-onyx-900/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-onyx-800/50">
                {["User", "Role", "Groups", "Team Access", "Actions"].map((h) => (
                  <th key={h} className="px-5 py-3 text-left text-xs font-display font-semibold text-onyx-500 tracking-widest uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} className="border-b border-onyx-800/20 hover:bg-onyx-800/20 transition-colors">
                  <td className="px-5 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-tiger-500 to-tiger-700 flex items-center justify-center flex-shrink-0">
                        <span className="text-xs font-display font-bold text-white">{u.username[0].toUpperCase()}</span>
                      </div>
                      <div>
                        <p className="font-body text-sm text-onyx-100">{u.username}</p>
                        {u.email && <p className="text-xs text-onyx-500">{u.email}</p>}
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-mono ${
                      u.level <= 2 ? "bg-tiger-500/15 text-tiger-400" :
                      u.level <= 4 ? "bg-blue-500/15 text-blue-400" :
                      u.level <= 6 ? "bg-green-500/15 text-green-400" :
                      "bg-onyx-700/30 text-onyx-400"
                    }`}>
                      {LEVEL_NAMES[u.level] || `L${u.level}`}
                    </span>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex flex-wrap gap-1">
                      {u.groups?.map((g) => (
                        <span key={g} className="px-1.5 py-0.5 text-xs font-mono bg-tiger-500/10 text-tiger-400 rounded">{g}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex flex-wrap gap-1 max-w-xs">
                      {u.team_grants?.map((tg) => (
                        <span key={tg.team_id} className="px-1.5 py-0.5 text-xs font-body bg-blue-500/10 text-blue-400 rounded" title={tg.permissions.map((p) => p.codename).join(", ")}>
                          {tg.team_name} ({tg.permissions.length})
                        </span>
                      ))}
                      {u.team_grants?.length === 0 && u.global_permissions?.length > 0 && (
                        <span className="text-xs text-onyx-600">Global only</span>
                      )}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    {me && (me.is_level_superuser || me.level < u.level) && u.id !== me.id && (
                      <button onClick={() => openGrants(u)}
                        className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-display tracking-wider text-tiger-400 bg-tiger-500/10 hover:bg-tiger-500/20 rounded-lg transition-colors">
                        <IconShield size={14} /> MANAGE
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create User Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Create User">
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className={labelCls}>Username</label>
            <input className={inputCls} value={newUser.username} onChange={(e) => setNewUser({ ...newUser, username: e.target.value })} required />
          </div>
          <div>
            <label className={labelCls}>Email</label>
            <input className={inputCls} type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} />
          </div>
          <div>
            <label className={labelCls}>Password</label>
            <input className={inputCls} type="password" value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} required />
          </div>
          <div>
            <label className={labelCls}>Role Level</label>
            <select className={inputCls} value={newUser.level} onChange={(e) => setNewUser({ ...newUser, level: parseInt(e.target.value) })}>
              {Object.entries(LEVEL_NAMES).filter(([lvl]) => me?.is_level_superuser || parseInt(lvl) > me?.level).map(([lvl, name]) => (
                <option key={lvl} value={lvl}>{name} (Level {lvl})</option>
              ))}
            </select>
          </div>
          <button type="submit" className={`w-full ${btnPrimary}`}>CREATE USER</button>
        </form>
      </Modal>

      {/* Grants + Groups Modal */}
      <Modal open={!!grantTarget} onClose={() => setGrantTarget(null)} title={`Manage — ${grantTarget?.username}`} wide>
        {grantTarget && (
          <div className="space-y-8">
            {/* ── GRANT NEW PERMISSION ── */}
            <div>
              <h3 className="flex items-center gap-2 text-sm font-display font-semibold text-tiger-400 tracking-widest mb-4">
                <IconPlus size={14} /> GRANT PERMISSION
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className={labelCls}>Permission</label>
                  <select className={inputCls} value={grantData.permission_id}
                    onChange={(e) => setGrantData({ ...grantData, permission_id: e.target.value })}>
                    <option value="">Select...</option>
                    {Object.entries(allPerms.reduce((acc, p) => {
                      (acc[p.category] = acc[p.category] || []).push(p); return acc;
                    }, {})).map(([cat, perms]) => (
                      <optgroup key={cat} label={cat.toUpperCase()}>
                        {perms.map((p) => (
                          <option key={p.id} value={p.id}>{p.codename} — {p.name}</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Team Scope</label>
                  <select className={inputCls} value={grantData.team_id}
                    onChange={(e) => setGrantData({ ...grantData, team_id: e.target.value })}>
                    <option value="">All Teams (Global)</option>
                    {allTeams.map((t) => (
                      <option key={t.id} value={t.id}>{t.name} ({t.sport_name})</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Expires At (optional)</label>
                  <input className={inputCls} type="datetime-local" value={grantData.expires_at}
                    onChange={(e) => setGrantData({ ...grantData, expires_at: e.target.value ? new Date(e.target.value).toISOString() : "" })} />
                  <p className="text-xs text-onyx-600 mt-1">Leave empty for permanent access</p>
                </div>
                <div>
                  <label className={labelCls}>Note</label>
                  <input className={inputCls} value={grantData.note} placeholder="e.g. Game day access"
                    onChange={(e) => setGrantData({ ...grantData, note: e.target.value })} />
                </div>
              </div>
              <button onClick={handleGrant} className={`mt-3 ${btnPrimary}`}>GRANT</button>
            </div>

            {/* ── ACTIVE GRANTS ── */}
            <div>
              <h3 className="flex items-center gap-2 text-sm font-display font-semibold text-onyx-300 tracking-widest mb-3">
                <IconShield size={14} /> ACTIVE GRANTS ({targetGrants.length})
              </h3>
              {targetGrants.length === 0 ? (
                <p className="text-sm text-onyx-600 font-body">No direct grants. Permissions come from group membership.</p>
              ) : (
                <div className="space-y-1.5 max-h-64 overflow-y-auto pr-1">
                  {targetGrants.map((g) => (
                    <div key={g.id} className={`flex items-center justify-between px-3 py-2.5 rounded-lg border transition-all ${
                      g.is_expired ? "border-red-500/20 bg-red-500/5 opacity-60" :
                      g.is_token ? "border-yellow-500/20 bg-yellow-500/5" :
                      "border-onyx-800/30 bg-onyx-800/20"
                    }`}>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-mono text-onyx-100">{g.permission_codename}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded font-mono ${
                            g.team_name ? "bg-blue-500/15 text-blue-400" : "bg-onyx-700/30 text-onyx-400"
                          }`}>
                            {g.scope_label}
                          </span>
                          {g.is_token && (
                            <span className="flex items-center gap-1 text-xs px-1.5 py-0.5 rounded bg-yellow-500/15 text-yellow-400">
                              <IconCalendar size={10} />
                              {g.is_expired ? "EXPIRED" : "TOKEN"}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5">
                          {g.expires_at && (
                            <span className="text-xs text-onyx-600">
                              {g.is_expired ? "Expired" : "Expires"}: {new Date(g.expires_at).toLocaleString()}
                            </span>
                          )}
                          {g.note && <span className="text-xs text-onyx-600 italic">"{g.note}"</span>}
                        </div>
                      </div>
                      <button onClick={() => handleRevoke(g.id)}
                        className="ml-2 p-1.5 text-red-400/60 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors flex-shrink-0">
                        <IconTrash size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* ── GROUPS ── */}
            <div>
              <h3 className="flex items-center gap-2 text-sm font-display font-semibold text-onyx-300 tracking-widest mb-3">
                <IconLayers size={14} /> ROLE GROUPS
              </h3>
              <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                {allGroups.map((g) => {
                  const canAssign = me?.is_level_superuser || g.level >= (me?.level || 99);
                  return (
                    <label key={g.id} className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${canAssign ? "hover:bg-onyx-800/40" : "opacity-40 cursor-not-allowed"}`}>
                      <input type="checkbox" checked={selectedGroupIds.includes(g.id)} disabled={!canAssign}
                        onChange={() => canAssign && toggleGroup(g.id)}
                        className="rounded border-onyx-600 text-tiger-500 focus:ring-tiger-500 bg-onyx-950" />
                      <div>
                        <p className="text-sm font-body text-onyx-200">{g.name}</p>
                        <p className="text-xs text-onyx-500">Level {g.level} · {g.permission_details?.length || 0} perms</p>
                      </div>
                    </label>
                  );
                })}
              </div>
              <button onClick={handleSaveGroups} className={`mt-3 ${btnPrimary}`}>SAVE GROUPS</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
